import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useCompany } from '../Company/CompanyContext';
import EosLogo from './EosLogo';
import SettingsModal from '../settings/SettingsModal';
import { motion, AnimatePresence } from 'framer-motion';

function TopBar({ onMenuClick }) {
  const [loggingOut, setLoggingOut] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  
  const { currentUser, logout } = useAuth();
  const { companyInfo } = useCompany();
  const navigate = useNavigate();
  
  // Ensure animation starts after component mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationComplete(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
      setLoggingOut(false);
    }
  };
  
  const handleOpenSettings = () => {
    setSettingsOpen(true);
  };
  
  const handleCloseSettings = () => {
    setSettingsOpen(false);
  };
  
  return (
    <>
      <motion.div 
        className="w-full bg-transparent backdrop-blur-lg p-3 flex items-center justify-between shadow-sm z-50"
        initial={{ y: -50, opacity: 0 }}
        animate={{ 
          y: 0, 
          opacity: 1,
          transition: {
            type: "spring",
            stiffness: 300,
            damping: 20,
            delay: 0.1,
          }
        }}
      >
        <div className="flex items-center">
          {/* Mobile menu button - only visible on small screens */}
          {onMenuClick && (
            <motion.button
              onClick={onMenuClick}
              className="mr-2 p-2 rounded-full text-orange-700 hover:text-orange-600 transition-all duration-300 md:hidden"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              aria-label="Menu"
              title="Menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </motion.button>
          )}
          
          <Link to="/" className="flex items-center group">
            <motion.div 
              className="transition-transform duration-300 transform group-hover:scale-105 h-10 w-20 flex items-center justify-center"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ 
                scale: 1, 
                opacity: 1,
                transition: {
                  type: "spring",
                  stiffness: 500,
                  damping: 15,
                  delay: 0.3,
                }
              }}
            >
              <EosLogo customLogoUrl={companyInfo?.customLogoUrl} />
            </motion.div>
            
          </Link>
        </div>
        
        {currentUser && (
          <div className="flex items-center space-x-3">
            <motion.button
              onClick={handleOpenSettings}
              className="text-orange-700 hover:text-orange-600 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:bg-orange-50/50 backdrop-blur-sm flex items-center"
              initial={{ opacity: 0, x: 20 }}
              animate={{ 
                opacity: 1, 
                x: 0,
                transition: {
                  delay: 0.5,
                  duration: 0.3
                }
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
              <span className="hidden sm:inline">Settings</span>
            </motion.button>
            <motion.button
              onClick={handleLogout}
              disabled={loggingOut}
              className="py-2 px-4 bg-orange-500 hover:bg-orange-600 text-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 flex items-center"
              initial={{ opacity: 0, x: 20 }}
              animate={{ 
                opacity: 1, 
                x: 0,
                transition: {
                  delay: 0.6,
                  duration: 0.3
                }
              }}
            >
              {loggingOut ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="hidden sm:inline">Logging out...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V7.414l-5-5H3zm7 2a1 1 0 10-2 0v4a1 1 0 102 0V5z" clipRule="evenodd" />
                    <path d="M11 14a1 1 0 100-2h-1a1 1 0 100 2h1z" />
                  </svg>
                  <span className="hidden sm:inline">Logout</span>
                  <span className="sm:hidden">Exit</span>
                </>
              )}
            </motion.button>
          </div>
        )}
      </motion.div>
      
      {/* Settings Modal */}
      <SettingsModal isOpen={settingsOpen} onClose={handleCloseSettings} />
    </>
  );
}

export default TopBar; 